import { useState } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

import api from "@/services/api";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    title: "Interview questions",
    description: "The same questions for every candidate, so you can compare them. Each question says what an Excellent, Solid, Borderline or Weak answer sounds like.",
    description_own: "Questions specific to a candidate, drawn from their resume, are added in their profile, Interview prep tab.",
    generate: "Generate from the criteria",
    generating: "Generating…",
    generate_confirm: "This replaces the current questions. Continue?",
    generated: "Questions generated",
    generate_error: "Failed to generate the questions",
    add: "+ Add a question",
    remove: "Remove",
    experience: "Past situation",
    situation: "Scenario",
    criterion: "Criterion tested",
    criterion_placeholder: "e.g. Credit decisions",
    question: "Question",
    question_placeholder: "Tell me about a time…",
    excellent: "Excellent",
    solid: "Solid",
    borderline: "Borderline",
    weak: "Weak",
    anchor_placeholder: "What an answer at this level sounds like",
    empty: "No questions yet. Generate them from the screening criteria, or add your own.",
    save: "Save",
    saving: "Saving…",
    saved: "Questions saved",
    error: "Failed to save the questions",
  },
  fr: {
    title: "Questions d'entretien",
    description: "Les mêmes questions pour tous les candidats, pour pouvoir les comparer. Chaque question dit à quoi ressemble une réponse Excellent, Solide, Limite ou Faible.",
    description_own: "Les questions propres à un candidat, tirées de son CV, s'ajoutent dans sa fiche, onglet Préparation entretien.",
    generate: "Générer à partir des critères",
    generating: "Génération…",
    generate_confirm: "Les questions actuelles seront remplacées. Continuer ?",
    generated: "Questions générées",
    generate_error: "Erreur lors de la génération",
    add: "+ Ajouter une question",
    remove: "Supprimer",
    experience: "Situation vécue",
    situation: "Mise en situation",
    criterion: "Critère évalué",
    criterion_placeholder: "ex. Décision de crédit",
    question: "Question",
    question_placeholder: "Racontez une fois où…",
    excellent: "Excellent",
    solid: "Solide",
    borderline: "Limite",
    weak: "Faible",
    anchor_placeholder: "À quoi ressemble une réponse de ce niveau",
    empty: "Aucune question. Générez-les à partir des critères de screening, ou ajoutez les vôtres.",
    save: "Enregistrer",
    saving: "Enregistrement…",
    saved: "Questions enregistrées",
    error: "Erreur lors de l'enregistrement",
  },
};

const KINDS = ["experience", "situation"];
const LEVELS = [
  { key: "excellent", cls: "text-green-700" },
  { key: "solid", cls: "text-blue-700" },
  { key: "borderline", cls: "text-amber-700" },
  { key: "weak", cls: "text-red-700" },
];
const EMPTY_ANCHORS = { excellent: "", solid: "", borderline: "", weak: "" };

// _id is kept so ratings already given on candidates stay attached to their question
const toRows = (questions) =>
  (questions || []).map((q) => ({ _id: q._id, kind: q.kind, criterion: q.criterion || "", question: q.question, anchors: { ...EMPTY_ANCHORS, ...q.anchors } }));

export default function Interview({ job, setJob }) {
  const { id } = useParams();
  const { t } = useComponentTranslation(translations);
  const [rows, setRows] = useState(toRows(job?.interview_questions));
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const cleaned = rows
    .map((r) => ({ ...r, criterion: r.criterion.trim(), question: r.question.trim() }))
    .filter((r) => r.question);
  const isDirty = JSON.stringify(cleaned) !== JSON.stringify(toRows(job?.interview_questions));

  const update = (index, patch) => setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const updateAnchor = (index, level, value) => update(index, { anchors: { ...rows[index].anchors, [level]: value } });

  const handleSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      const { ok, data } = await api.put(`/job/${id}`, { interview_questions: cleaned });
      if (!ok) return toast.error(t("error"));
      setJob(data);
      setRows(toRows(data.interview_questions));
      toast.success(t("saved"));
    } catch (e) {
      console.error(e);
      toast.error(t("error"));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (rows.length && !window.confirm(t("generate_confirm"))) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/job/${id}/generate-interview-questions`);
      setJob(data);
      setRows(toRows(data.interview_questions));
      toast.success(t("generated"));
    } catch (e) {
      console.error(e);
      toast.error(t("generate_error"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container mx-auto max-w-3xl p-6 pb-20">
      <h2 className="text-lg font-semibold text-gray-900">{t("title")}</h2>
      <p className="text-sm text-gray-600 mt-1">{t("description")}</p>
      <p className="text-sm text-gray-500 mt-1">{t("description_own")}</p>

      <div className="flex flex-wrap gap-3 mt-5 mb-8">
        <button type="button" onClick={handleGenerate} disabled={generating} className="btn btn-secondary disabled:opacity-50">
          {generating ? t("generating") : t("generate")}
        </button>
        <button type="button" onClick={() => setRows([...rows, { kind: "experience", criterion: "", question: "", anchors: { ...EMPTY_ANCHORS } }])} className="btn btn-secondary">
          {t("add")}
        </button>
      </div>

      {rows.length === 0 && <p className="text-sm text-gray-500 mb-8">{t("empty")}</p>}

      {rows.map((row, index) => (
        <section key={row._id || `new-${index}`} className="pb-8 mb-8 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-xs font-semibold text-gray-500">{index + 1}</span>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label={t("question")}>
              {KINDS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  aria-pressed={row.kind === kind}
                  onClick={() => update(index, { kind })}
                  className={`px-3 h-8 text-xs font-medium border-r border-gray-200 last:border-r-0 ${row.kind === kind ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                >
                  {t(kind)}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={row.criterion}
              onChange={(e) => update(index, { criterion: e.target.value })}
              placeholder={t("criterion_placeholder")}
              aria-label={t("criterion")}
              className="input flex-1 min-w-[200px] h-8 text-sm"
            />
            <button type="button" onClick={() => setRows(rows.filter((_, i) => i !== index))} className="text-sm text-gray-500 hover:text-gray-800">
              {t("remove")}
            </button>
          </div>

          <textarea
            value={row.question}
            onChange={(e) => update(index, { question: e.target.value })}
            placeholder={t("question_placeholder")}
            aria-label={t("question")}
            rows={2}
            className="w-full border-0 border-l-[3px] border-primary pl-4 py-1 text-base text-gray-900 resize-none focus:outline-none focus:ring-0"
          />

          <div className="mt-3 space-y-1.5">
            {LEVELS.map(({ key, cls }) => (
              <label key={key} className="flex items-baseline gap-3 text-sm">
                <span className={`w-20 shrink-0 font-semibold ${cls}`}>{t(key)}</span>
                <input
                  type="text"
                  value={row.anchors[key]}
                  onChange={(e) => updateAnchor(index, key, e.target.value)}
                  placeholder={t("anchor_placeholder")}
                  className="flex-1 border-0 border-b border-transparent hover:border-gray-200 focus:border-primary py-1 text-sm text-gray-700 focus:outline-none bg-transparent"
                />
              </label>
            ))}
          </div>
        </section>
      ))}

      <div className="flex justify-end">
        <button type="button" onClick={handleSave} disabled={!isDirty || saving} className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? t("saving") : t("save")}
        </button>
      </div>
    </div>
  );
}
